// BlockPropertiesSection — namn + description + (ev. email-fields) +
// färg + save/delete för ett valt block. Verbatim extraherad ur
// PropertiesPanel.tsx (#8, POST_QA_SUBPLAN Wave E.1 step 3).
//
// State + behavior:
//   - localConfig + updateLocalConfig är kontrollerade props
//   - onUpdateBlock används direkt för färgen (commits utan save-knapp;
//     färg är atomic per pick, matchar original-beteendet)
//   - blockNameSuggestionsSlot + crossSheetLinksSlot är ReactNode-slots
//     som PropertiesPanel-orchestrator renderar. Då slipper denna
//     komponent känna till dependencies som BlockNameSuggestions +
//     CrossSheetLinksSection — den förblir ren JSX för panel-fältet.

import type { ReactNode } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/i18n/LanguageContext';
import type { BlockConfig, WorkflowBlock } from '@/types/workflow';

interface BlockPropertiesSectionProps {
  selectedBlock: WorkflowBlock;
  localConfig: BlockConfig;
  updateLocalConfig: (key: string, value: string) => void;
  onUpdateBlock: (id: string, patch: Partial<WorkflowBlock>) => void;
  onSave: () => void;
  onDelete: () => void;
  /** Render-slot: name-suggestions UI under name-input. */
  blockNameSuggestionsSlot?: ReactNode;
  /** Render-slot: cross-sheet-links UI under action-knapparna.
   *  Lämnas tom när workspace har <2 sheets. */
  crossSheetLinksSlot?: ReactNode;
}

export function BlockPropertiesSection({
  selectedBlock, localConfig, updateLocalConfig, onUpdateBlock,
  onSave, onDelete,
  blockNameSuggestionsSlot, crossSheetLinksSlot,
}: BlockPropertiesSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="space-y-2" data-guide="properties-name">
        <Label htmlFor="block-name">{t('name')}</Label>
        <Input
          id="block-name"
          value={localConfig.name}
          maxLength={200}
          onChange={(e) => updateLocalConfig('name', e.target.value)}
          aria-invalid={!localConfig.name.trim() || undefined}
          aria-describedby={!localConfig.name.trim() ? "block-name-error" : undefined}
        />
        {!localConfig.name.trim() && (
          <p id="block-name-error" className="text-xs text-destructive">Name is required</p>
        )}
        {blockNameSuggestionsSlot}
      </div>

      <div className="space-y-2" data-guide="properties-description">
        <Label htmlFor="block-description">{t('description')}</Label>
        <Textarea
          id="block-description"
          value={localConfig.description || ''}
          onChange={(e) => updateLocalConfig('description', e.target.value)}
          rows={4}
          placeholder={t('writeDescription')}
        />
      </div>

      {selectedBlock.type === 'send-email' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="email-to">{t('recipient')}</Label>
            <Input
              id="email-to"
              value={localConfig.to || ''}
              onChange={(e) => updateLocalConfig('to', e.target.value)}
              placeholder="{{manager.email}}"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-subject">{t('subject')}</Label>
            <Input
              id="email-subject"
              value={localConfig.subject || ''}
              onChange={(e) => updateLocalConfig('subject', e.target.value)}
              placeholder="Välkommen!"
            />
          </div>
        </>
      )}

      <div className="space-y-2" data-guide="properties-color">
        <Label>{t('blockColor')}</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={selectedBlock.color}
            onChange={(e) => onUpdateBlock(selectedBlock.id, { color: e.target.value })}
            className="w-10 h-10 rounded-lg border border-border cursor-pointer"
            aria-label={t('blockColor')}
            data-testid="block-color-picker"
          />
          <Input
            value={selectedBlock.color}
            onChange={(e) => onUpdateBlock(selectedBlock.id, { color: e.target.value })}
            className="flex-1"
            aria-label={`${t('blockColor')} hex value`}
            data-testid="block-color-hex"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-2" data-guide="properties-actions">
        <Button onClick={onSave} className="flex-1" data-testid="save-block">
          <Save className="w-4 h-4 mr-2" aria-hidden="true" />
          {t('save')}
        </Button>
        <Button variant="destructive" onClick={onDelete} aria-label={t('deleteBlock') || 'Delete block'} data-testid="delete-block">
          <Trash2 className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      {crossSheetLinksSlot}
    </div>
  );
}
